import React, { useContext } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import HomeRoundedIcon          from '@mui/icons-material/HomeRounded';
import AnalyticsRoundedIcon     from '@mui/icons-material/AnalyticsRounded';
import BuildRoundedIcon         from '@mui/icons-material/BuildRounded';
import TuneRoundedIcon          from '@mui/icons-material/TuneRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import BookRoundedIcon          from '@mui/icons-material/BookRounded';
import SettingsRoundedIcon      from '@mui/icons-material/SettingsRounded';
import { AuthContext } from '../components/signin/AuthContext';
import { useConfig }   from '../components/ConfigContext';
import { useBasePath } from './theme';

/* Navigation entries shared by all UI variants; paths are relative to the variant's base path. */
export const NAV_ITEMS = [
  { label: 'Dashboard',     icon: HomeRoundedIcon,          path: '/dashboard'   },
  { label: 'Analytics',     icon: AnalyticsRoundedIcon,     path: '/analytics'   },
  { label: 'Konfig',        icon: BuildRoundedIcon,         path: '/config'      },
  { label: 'Aktoren',       icon: TuneRoundedIcon,          path: '/actorconfig' },
  { label: 'Alerts',        icon: NotificationsRoundedIcon, path: '/alertconfig' },
  { label: 'Grow Plan',     icon: BookRoundedIcon,          path: '/grow_plan'   },
  { label: 'Einstellungen', icon: SettingsRoundedIcon,      path: '/settings'    },
];

function tokenValid(t) {
  try { return Date.now() / 1000 < jwtDecode(t).exp; }
  catch { return false; }
}

/** Sign-in route of the active variant. */
export function useSignInPath() {
  const base = useBasePath();
  return base ? `${base}/signin` : '/';
}

/** Renders children only with a valid JWT, otherwise redirects to the variant's sign-in. */
export function RequireAuth({ children }) {
  const { token, setToken } = useContext(AuthContext);
  const signIn = useSignInPath();

  if (!token || !tokenValid(token)) {
    localStorage.setItem('authenticated', 'false');
    localStorage.setItem('JWT', '');
    setToken(null);
    return <Navigate to={signIn} replace />;
  }
  return children;
}

/** Everything a navigation shell needs: routing, unsaved config changes, user, logout. */
export function useShell() {
  const base     = useBasePath();
  const signIn   = useSignInPath();
  const location = useLocation();
  const navigate = useNavigate();
  const { setToken, avatar, username } = useContext(AuthContext);
  const { changes, saveConfig } = useConfig();

  return {
    items: NAV_ITEMS,
    isActive: (path) => location.pathname === base + path,
    go: (path) => navigate(base + path),
    current: NAV_ITEMS.find(i => location.pathname === base + i.path) || null,
    changes,
    unsavedCount: Object.keys(changes).length,
    saveConfig,
    avatar,
    username,
    logout: () => { setToken(null); navigate(signIn); },
  };
}
